import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMainStore } from "@/stores";
import StatCard from "@/features/dashboard/components/StatCard";
import DashboardHeader from "@/features/dashboard/components/DashboardHeader";
import WhiteBgCard from "@/features/dashboard/components/WhiteBgCard";
import { CircleCheckBig, Clipboard, Users } from "lucide-react";
import { useFetchUsersList, useReviewTrialRequest } from "@/shared/hooks";
import Loader from "@/shared/components/ui/Loaders/Loader";
import {
  ACCESS_REQUEST_COLUMNS,
  createAccessRequestRenderCell,
} from "@/shared/utils/commonConfigs";
import { formatDate } from "@/shared/utils/helperFunctions";
import CommonCardLayout from "@/features/dashboard/components/CommonCardLayout";
import CustomTable from "@/shared/components/ui/Button/CustomTable";

const ICON_MAP = {
  users: <Users className="text-secondary" size={22} strokeWidth={1.5} />,
  pending: <Users className="text-warning-500" size={22} strokeWidth={1.5} />,
  campaigns: (
    <Clipboard className="text-success-500" size={22} strokeWidth={1.5} />
  ),
  plan: (
    <CircleCheckBig className="text-success-500" size={22} strokeWidth={1.5} />
  ),
};

const OrgAdminDashboard = () => {
  const { user } = useMainStore();
  const [page, setPage] = useState(1);
  const [pendingRowsPerPage, setPendingRowsPerPage] = useState(5);

  const { data: allUserList, isLoading } = useFetchUsersList({
    limit: 100,
    offset: 0,
    enabled: !!user?.id,
  });

  const { mutate: reviewTrialRequest, isPending: isPendingReviewTrialRequest } =
    useReviewTrialRequest();

  const handleReviewTrialRequest = useCallback(
    (userId, action) => reviewTrialRequest({ userId, action }),
    [reviewTrialRequest],
  );

  const accessRequestRenderCell = useMemo(
    () =>
      createAccessRequestRenderCell(
        handleReviewTrialRequest,
        isPendingReviewTrialRequest,
      ),
    [handleReviewTrialRequest, isPendingReviewTrialRequest],
  );

  const {
    stats,
    pendingUsers,
    approvedMembers,
    memberBreakdown,
    recentMembers,
  } = useMemo(() => {
    const list = allUserList ?? [];
    const approved = list.filter((u) => u.trial_approved_at != null);
    const pending = list.filter(
      (u) => u.trial_approved_at == null && u.plan !== "reject",
    );

    const byRole = {};
    approved.forEach((u) => {
      const role =
        u.role === "org_admin"
          ? "Org Admin"
          : u.role === "user"
            ? "User"
            : "Member";
      byRole[role] = (byRole[role] || 0) + 1;
    });
    const breakdown = Object.entries(byRole).map(([role, count]) => ({
      role,
      count,
    }));

    const recent = [...approved]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5)
      .map((m) => ({
        ...m,
        joinedDate: formatDate(m.created_at),
      }));

    const planType =
      list[0]?.plan?.charAt(0).toUpperCase() +
        (list[0]?.plan?.slice(1) || "") || "Trial";

    return {
      stats: [
        { title: "Total Members", value: approved.length, icon: "users" },
        { title: "Pending Requests", value: pending.length, icon: "pending" },
        { title: "Active Campaigns (coming soon)", value: 0, icon: "campaigns" },
        { title: "Plan Type", value: planType, icon: "plan" },
      ],
      pendingUsers: pending,
      approvedMembers: approved,
      memberBreakdown: breakdown,
      recentMembers: recent,
    };
  }, [allUserList]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <Loader text="Loading Org Admin Dashboard..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <DashboardHeader
        userName={user?.name}
        roleBadge="Organization Admin"
        subtitle="Manage your team and workspace"
      />

      <div className="flex flex-col gap-6 h-[calc(100vh-120px)] overflow-y-auto px-1">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              title={stat.title}
              value={stat.value}
              icon={ICON_MAP[stat.icon]}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">
          <CommonCardLayout title="Pending User Join Requests">
            <div className="relative h-full min-h-0">
              {pendingUsers.length === 0 ? (
                <p className="text-gray-500 text-sm py-4 text-center h-full flex items-center justify-center">
                  No pending user join requests.
                </p>
              ) : (
                <CustomTable
                  columns={ACCESS_REQUEST_COLUMNS}
                  data={pendingUsers}
                  renderCell={accessRequestRenderCell}
                  rowsPerPage={pendingRowsPerPage}
                  setRowsPerPage={setPendingRowsPerPage}
                  totalCount={pendingUsers?.length}
                  page={page}
                  onPageChange={setPage}
                  height="100%"
                />
              )}
            </div>
          </CommonCardLayout>

          <div className="flex flex-col gap-3.5 ">
            <h3 className="text-[0.85rem] font-bold text-gray-800 font-inter">
              Team Overview
            </h3>
            <WhiteBgCard>
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap gap-3">
                  {memberBreakdown.map(({ role, count }) => (
                    <div
                      key={role}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg"
                    >
                      <span className="text-sm font-medium text-gray-800">
                        {role}
                      </span>
                      <span className="text-sm text-gray-500">({count})</span>
                    </div>
                  ))}
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 mb-2">
                    Recent Joins
                  </h4>
                  <ul className="space-y-2">
                    {recentMembers.length > 0 ? (
                      recentMembers.map((member) => (
                        <li
                          key={member.id}
                          className="flex justify-between items-center text-sm"
                        >
                          <span className="font-medium text-gray-800">
                            {member.name}
                          </span>
                          <span className="text-gray-500">
                            {member.joinedDate}
                          </span>
                        </li>
                      ))
                    ) : (
                      <li className="text-sm text-gray-500">No members yet.</li>
                    )}
                  </ul>
                </div>
                <Link
                  to="/oa/members"
                  className="text-secondary text-sm font-medium hover:underline mt-2 block"
                >
                  View all members →
                </Link>
              </div>
            </WhiteBgCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrgAdminDashboard;
